import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createDateRange,
  getFormattedTimeStringFromPeriod,
  noBarData,
  noData,
  type PerformanceWithAccountFilter,
  validLineData,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import {
  getRemedialTrainingEngagement,
  getRemedialTrainingLineChart,
} from "../../queries/threatDefensePerformanceQueries";
import { useChartPeriodMeasure, CHART_PERIOD } from "hooks/useChartPeriodMeasure";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { ChartWrapper, HorizontalBarChart, LineChart } from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import CardReporting from "components/cards/CardReporting/CardReporting";
import styles from "./ThreatDefensePhishingCampaignBody.module.scss"; // Reusing same styles
import { ThreatDefenseFlowStatusEnum } from "../types";
import UsersTable from "./UsersTable";

const activityColorScale = [reportingColors.greyLight, reportingColors.yellow, reportingColors.greenLight];

export type Props = {
  filter: PerformanceWithAccountFilter;
  campaignId: number;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

export const ThreatDefenseRemedialTrainingBody = ({
  filter,
  campaignId,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryFn: getRemedialTrainingLineChart,
    queryKey: ["remedialTrainingLineChart", filter, chartPeriod, campaignId],
    enabled: chartPeriod !== CHART_PERIOD.UNSET,
  });

  const engagementQuery = useQuery({
    queryFn: getRemedialTrainingEngagement,
    queryKey: ["remedialTrainingEngagement", filter, campaignId],
  });

  return (
    <div className={styles.root}>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["Activity"]}
          showLegend
          legendLabels={[
            ThreatDefenseFlowStatusEnum.NOT_STARTED,
            ThreatDefenseFlowStatusEnum.IN_PROGRESS,
            ThreatDefenseFlowStatusEnum.COMPLETED,
          ]}
          colorRange={activityColorScale}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess && validLineData(lineChartQuery.data.lineData.yData) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xData={lineChartQuery.data.lineData.xData}
                yData={lineChartQuery.data.lineData.yData}
                xFormatterFunc={(d: string) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
                colorRange={activityColorScale}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.engagement}>
        <ChartWrapper titles={["Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.barData.map((d) => d.value)) ? (
              <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementQuery.data.barData} />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <h2 className={styles.sessionsHeader}>People</h2>
        <UsersTable
          filter={filter}
          campaignId={campaignId}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      </div>
    </div>
  );
};
